import { request } from '@@/plugin-request/request';
import { trimAndFilterEmptyParams } from '@/utils/request';
import { RequestData } from '@ant-design/pro-table';
import { CardHolderInfoData, SupportingDocumentType } from './cards';
import { normalizeEmailFields } from '@/utils/email';

export interface CardApplicationItem {
  userId: string;
  accountNumber: string;
  countryCode: string;
  phone: string;
  email: string;
  idNumber: string;
  kycLevel: {
    id: number;
    name: string;
  };
  metadata: object;
  name: string;
  relationshipManager: object;
  vendor: {
    id: number;
    name: string;
  };
  application: Application[];
  solutionName: string;
  /** User's account type (e.g., COBRAND, PERSONAL, CORPORATE) */
  accountType: string;
}
export interface Application {
  id: string;
  cardId?: string;
  cardEmbossedName: string;
  cardPhone: string;
  cardEmail?: string;
  cardProduct: string;
  issueProductId?: string;
  applicationTime: string;
  delivery_method: string;
  status: string;
  cardLimit: number;
  operator?: string;
  address?: string;
  deliveryContactPerson?: string | null;
  deliveryContactPhone?: string | null;
  deliveryContactEmail?: string | null;
  deliveryAddress?: string | null;
  cardApplicationRemark?: string;
  creditLineId?: string;
  cardAccountNumber?: string;
  cardAccountName?: string;
  cardAccountType?: string;
  kycBypass?: boolean;
  clientIdentityId?: string;
  clientIdentityInfo?: CardHolderInfoData;
  cardKyc?: PublicCardKyc | null;
  renewCard?: boolean;
  replacementCard?: boolean;
  decisionInfo?: {
    renewWithSameNumber: boolean;
  };
}

export type PublicCardKyc = {
  status?: 'pending_approval' | 'approved' | 'rejected';
  rejectedReason: string | null;
  rejectionAdditionalInfo: string | null;
};

export type VendorCardKycIdentityDraft = {
  firstName?: string;
  lastName?: string;
  firstNameLocal?: string | null;
  lastNameLocal?: string | null;
  email?: string;
  mobile?: string | null;
  position?: string;
  occupation?: string;
  dateOfBirth?: string;
  annualIncome?: number | null;
  address?: string | null;
  gender?: 'MALE' | 'FEMALE' | null;
  nationality?: string | null;
  document?: {
    type?: 'passport' | 'drivers-license' | 'national-id';
    number?: string;
    country?: string;
    expiryDate?: string | null;
    front?: string | null;
    back?: string | null;
  };
  supportingDocuments?: Array<{
    documentType: SupportingDocumentType;
    file?: string | null;
  }>;
  removedSupportingDocumentIndexes?: number[];
};

export type VendorCardKycFile = {
  url: string | null;
  contentType: string | null;
};

export type VendorCardKycDetail = {
  applicationId: string;
  cardKyc: PublicCardKyc;
  canUpdate: boolean;
  expectedIdentityUpdatedAt: string;
  cardAccount: {
    id: string;
    accountNumber: string;
    name: string;
  } | null;
  cardHolderInfo: {
    firstName: string;
    lastName: string;
    firstNameLocal?: string | null;
    lastNameLocal?: string | null;
    email: string | null;
    mobile?: string | null;
    position?: string | null;
    occupation?: string | null;
    dateOfBirth: string | null;
    annualIncome?: number | null;
    address?: string | null;
    gender?: 'MALE' | 'FEMALE' | null;
    nationality?: string | null;
    document?: {
      type: 'passport' | 'drivers-license' | 'national-id';
      number: string;
      country: string;
      expiryDate?: string | null;
      front: VendorCardKycFile | null;
      back: VendorCardKycFile | null;
    };
    supportingDocuments?: Array<{
      index: number;
      documentType: SupportingDocumentType;
      file: VendorCardKycFile;
    }>;
  };
};

export type VendorCardKycTrackingItem = {
  applicationId: string;
  applicationStatus: string;
  applicationTime: string;
  cardProduct: string;
  user: {
    id: string;
    name: string;
    accountNumber: string;
  };
  cardKyc: PublicCardKyc | null;
  canUpdate: boolean;
};

export enum CardApplicationStatus {
  NEW = 'NEW',
  PENDING_KYC_APPROVAL = 'PENDING_KYC_APPROVAL',
  ISSUED = 'ISSUED',
  CANCELLED = 'CANCELLED',
  REJECTED = 'REJECTED',
  FAILED = 'FAILED'
}
export async function getCardApplicationList(params: any): Promise<RequestData<CardApplicationItem>> {
  let data = await request<RequestData<CardApplicationItem>>('/cms/cards/application/list', {
    params: normalizeEmailFields(params, ['email'])
  });
  return data;
}

export async function getVendorCardKycTracking(params: any): Promise<RequestData<VendorCardKycTrackingItem>> {
  return request<RequestData<VendorCardKycTrackingItem>>('/cms/cards/application/kyc', {
    method: 'GET',
    params: trimAndFilterEmptyParams(params)
  });
}

export async function getVendorCardKycDetail(applicationId: string): Promise<VendorCardKycDetail> {
  return request<VendorCardKycDetail>(`/cms/cards/application/${applicationId}/kyc`, {
    method: 'GET'
  });
}

export async function updateVendorCardKycSubmission(
  applicationId: string,
  expectedIdentityUpdatedAt: string,
  data: VendorCardKycIdentityDraft
): Promise<{ cardKyc: PublicCardKyc }> {
  return request(`/cms/cards/application/${applicationId}/kyc`, {
    method: 'POST',
    data: {
      expectedIdentityUpdatedAt,
      submission: data
    }
  });
}

export type CardApplicationQuotaRaiseRequest = {
  id: string;
  user: {
    id: string;
    name: string;
    accountNumber: string;
  };
  rm: string | null;
  currentQuota: number;
  requestedQuota: number;
  status: string;
  requestedAt: string;
  requestorNote: string;
  approverNote: string;
  approvedAt: string | null;
  rejectedAt: string | null;
  approvers: string[];
  cancelledAt: string | null;
  refId: string;
  requestedBy: string;
  approvedBy?: string;
  rejectedBy?: string;
  cancelledBy?: string;
};

export type GetCardApplicationQuotaRaiseRequestsDto = {
  current?: number;
  pageSize?: number;
  userId?: string;
  status?: string;
  relationshipManagerId?: string;
  vendorId?: string;
};

export async function getCardApplicationQuotaRaiseRequests(
  params: GetCardApplicationQuotaRaiseRequestsDto
): Promise<RequestData<CardApplicationQuotaRaiseRequest>> {
  return request('/cms/cards/application/quota-raise-requests', {
    method: 'get',
    params: trimAndFilterEmptyParams(params)
  });
}
